/**
 * Browser-direct AWS Transcribe STREAMING (faster voice, ~1-2s with live partials).
 *
 * Flow: mic PCM (pcm-capture) -> live chunk queue -> Amazon Transcribe streaming
 * (browser-direct, temp creds vended by the Cognito Identity Pool) -> partial/final
 * transcript events. Audio never leaves our AWS account (no Google / no 3rd-party
 * STT — memory: feedback_voice_capture_privacy).
 *
 * The heavy AWS SDK packages are loaded via dynamic import() so Vite code-splits
 * them into a separate async chunk that never touches first paint.
 *
 * Pure, testable units (exported): createAudioQueue (the capture->SDK bridge) and
 * consumeTranscriptStream (partial/final parsing + final accumulation). The
 * orchestrator startStreamTranscribe wires the real SDK + mic and is covered by
 * the live smoke.
 */

import { getAccessToken } from './auth';
import {
  COGNITO_IDENTITY_POOL_ID,
  COGNITO_REGION,
  cognitoLoginKey,
  isVoiceStreamingConfigured,
} from './voice-stream-config';
import { startStreamCapture, isPcmCaptureSupported, type StreamCaptureHandle } from './audio/pcm-capture';

/** One Transcribe AudioStream event. */
export interface AudioEventLike {
  AudioEvent: { AudioChunk: Uint8Array };
}

/**
 * Async queue bridging the mic's onChunk callback to the SDK's AudioStream
 * async-iterable. push() while recording; finish() on stop -> the generator
 * drains remaining chunks then returns (signals end-of-audio to Transcribe).
 */
export function createAudioQueue() {
  const chunks: Uint8Array[] = [];
  let wake: (() => void) | null = null;
  let done = false;

  return {
    push(chunk: Uint8Array) {
      if (done) return;
      chunks.push(chunk);
      if (wake) { wake(); wake = null; }
    },
    finish() {
      done = true;
      if (wake) { wake(); wake = null; }
    },
    async *stream(): AsyncGenerator<AudioEventLike> {
      for (;;) {
        if (chunks.length > 0) {
          yield { AudioEvent: { AudioChunk: chunks.shift()! } };
          continue;
        }
        if (done) return;
        await new Promise<void>((resolve) => { wake = resolve; });
      }
    },
  };
}

export interface TranscriptHandlers {
  onPartial?: (text: string) => void;
  onFinal?: (text: string) => void;
}

/** A minimal shape of a Transcribe streaming result event (for typing + tests). */
export interface TranscriptResultEventLike {
  TranscriptEvent?: {
    Transcript?: {
      Results?: Array<{
        IsPartial?: boolean;
        Alternatives?: Array<{ Transcript?: string }>;
      }>;
    };
  };
}

/**
 * Consume the Transcribe result stream: emit partials live, accumulate the
 * final segments, return the full final transcript. Pure over any async-iterable
 * of result events (real SDK stream OR a fake in tests).
 */
export async function consumeTranscriptStream(
  stream: AsyncIterable<TranscriptResultEventLike>,
  handlers: TranscriptHandlers = {},
): Promise<string> {
  const finals: string[] = [];
  let lastPartial = '';
  // The live display must show the FULL running transcript — every finalized
  // segment so far PLUS the in-progress partial — not just the latest segment.
  // AWS Transcribe finalizes speech in chunks, so the last partial alone is often
  // just the trailing word (e.g. "45"), which looks like it only caught part.
  const running = () => [...finals, lastPartial].join(' ').replace(/\s+/g, ' ').trim();
  for await (const event of stream) {
    const results = event.TranscriptEvent?.Transcript?.Results ?? [];
    for (const result of results) {
      const text = result.Alternatives?.[0]?.Transcript ?? '';
      if (!text) continue;
      if (result.IsPartial) {
        lastPartial = text;
      } else {
        finals.push(text);
        lastPartial = '';
        handlers.onFinal?.(text);
      }
      handlers.onPartial?.(running()); // always emit the whole running sentence
    }
  }
  return running();
}

export interface StreamTranscribeOptions {
  onPartial?: (text: string) => void;
  /** Fires when the max-session guard auto-stops the mic — wire to the UI's stop flow. */
  onAutoStop?: () => void;
  /** Hard cap on a single session (cost guard). Default 180s — plenty for an expense. */
  maxSeconds?: number;
}

export interface StreamTranscribeController {
  /** Stop the mic, drain the final result, return the transcript + duration. */
  stop: () => Promise<{ transcript: string; seconds: number }>;
  /** Immediately release the mic + tear down, discarding any transcript (cancel/unmount). */
  abort: () => void;
}

/** True when streaming voice is both configured AND supported in this browser. */
export function canStreamVoice(): boolean {
  return isVoiceStreamingConfigured() && isPcmCaptureSupported();
}

// Lazy-loaded SDK handles (code-split). Optionally injectable for tests.
interface VoiceStreamSdk {
  fromCognitoIdentityPool: (args: unknown) => unknown;
  TranscribeStreamingClient: new (cfg: unknown) => {
    send: (cmd: unknown) => Promise<{ TranscriptResultStream?: AsyncIterable<TranscriptResultEventLike> }>;
    destroy?: () => void;
  };
  StartStreamTranscriptionCommand: new (input: unknown) => unknown;
}

let _sdkOverride: VoiceStreamSdk | null = null;
let _captureOverride: typeof startStreamCapture | null = null;

/** Test seam: inject a fake SDK + capture starter. */
export function _setVoiceStreamTestDeps(deps: {
  sdk?: VoiceStreamSdk | null;
  startCapture?: typeof startStreamCapture | null;
}) {
  _sdkOverride = deps.sdk ?? null;
  _captureOverride = deps.startCapture ?? null;
}

async function loadSdk(): Promise<VoiceStreamSdk> {
  if (_sdkOverride) return _sdkOverride;
  const [streaming, creds] = await Promise.all([
    import('@aws-sdk/client-transcribe-streaming'),
    import('@aws-sdk/credential-provider-cognito-identity'),
  ]);
  return {
    fromCognitoIdentityPool: creds.fromCognitoIdentityPool as unknown as VoiceStreamSdk['fromCognitoIdentityPool'],
    TranscribeStreamingClient: streaming.TranscribeStreamingClient as unknown as VoiceStreamSdk['TranscribeStreamingClient'],
    StartStreamTranscriptionCommand: streaming.StartStreamTranscriptionCommand as unknown as VoiceStreamSdk['StartStreamTranscriptionCommand'],
  };
}

/**
 * Start a live streaming transcription. Resolves once the stream is open and
 * capturing; the returned controller's stop() returns the final transcript.
 * Throws on any failure (no config, no mic, bad creds, stream error) so the
 * caller can fall back to the batch path.
 */
export async function startStreamTranscribe(
  opts: StreamTranscribeOptions = {},
): Promise<StreamTranscribeController> {
  if (!isVoiceStreamingConfigured()) {
    throw new Error('voice streaming not configured');
  }

  const idToken = await getAccessToken();
  if (!idToken) throw new Error('not authenticated');

  const sdk = await loadSdk();
  const captureStarter = _captureOverride ?? startStreamCapture;

  const credentials = sdk.fromCognitoIdentityPool({
    identityPoolId: COGNITO_IDENTITY_POOL_ID,
    logins: { [cognitoLoginKey()]: idToken },
    clientConfig: { region: COGNITO_REGION },
  });

  const client = new sdk.TranscribeStreamingClient({ region: COGNITO_REGION, credentials });
  const queue = createAudioQueue();

  // Start the mic first; if it throws (no permission), nothing else has spun up.
  const capture: StreamCaptureHandle = await captureStarter((pcm) => queue.push(pcm));

  const startedAt = Date.now();
  let maxTimer: ReturnType<typeof setTimeout> | undefined;
  let autoStopped = false;
  // Keep the latest full running transcript so stop() can return instantly with
  // what we already have if Transcribe is slow to flush its final result.
  let latestTranscript = '';

  // Stop the mic + signal end-of-audio to Transcribe (idempotent via the inner flags).
  const finishAudio = () => {
    try { capture.stop(); } catch { /* noop */ }
    try { queue.finish(); } catch { /* noop */ }
  };

  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    if (maxTimer) { clearTimeout(maxTimer); maxTimer = undefined; }
    finishAudio();
    try { client.destroy?.(); } catch { /* noop */ }
  };

  let response;
  try {
    const command = new sdk.StartStreamTranscriptionCommand({
      LanguageCode: 'en-US',
      MediaEncoding: 'pcm',
      MediaSampleRateHertz: 16000,
      AudioStream: queue.stream(),
    });
    response = await client.send(command);
  } catch (err) {
    cleanup();
    throw err instanceof Error ? err : new Error(String(err));
  }

  const resultStream = response.TranscriptResultStream;
  if (!resultStream) {
    cleanup();
    throw new Error('no transcript stream');
  }

  // Drive the result stream in the background; accumulate the final transcript.
  // Surface (don't swallow) a mid-stream failure so silence ≠ error in the logs.
  const consumePromise = consumeTranscriptStream(resultStream, {
    onPartial: (t) => { latestTranscript = t; opts.onPartial?.(t); },
  }).catch((e) => {
    console.warn('voice stream ended with error:', e instanceof Error ? e.message : e);
    return latestTranscript;
  });

  // Max-session cost guard: a stuck/walked-away session can otherwise stream to
  // Transcribe's 2-hour limit. At maxSeconds, cap the audio (releases the mic +
  // flushes the final) and notify the UI so it can finalize the capture.
  const maxMs = Math.max(10, opts.maxSeconds ?? 180) * 1000;
  maxTimer = setTimeout(() => {
    if (cleaned) return;
    autoStopped = true;
    finishAudio();
    maxTimer = undefined;
    try { opts.onAutoStop?.(); } catch { /* noop */ }
  }, maxMs);

  return {
    stop: async () => {
      if (maxTimer) { clearTimeout(maxTimer); maxTimer = undefined; }
      // Stop the mic + signal end-of-audio so Transcribe flushes its final
      // (no-op if the max-session guard already did it).
      if (!autoStopped) finishAudio();
      // Wait briefly for Transcribe's final flush, but if it's slow, return the
      // full text we already captured rather than making the user wait.
      const transcript = await Promise.race<string>([
        consumePromise,
        new Promise<string>((resolve) => setTimeout(() => resolve(latestTranscript), 5000)),
      ]);
      cleanup();
      const seconds = Math.max(0, Math.round((Date.now() - startedAt) / 1000));
      return { transcript: (transcript || '').trim(), seconds };
    },
    abort: () => {
      // Immediate teardown — releases the mic now, discards the transcript.
      cleanup();
    },
  };
}
